import { ImgRectangle } from "../drawing/ImgRectangle"
import { RoundedRect } from "../drawing/Rectangle"
import { IRadius, Side, TranslateFunction, zeroIRadius } from "../models/Common"
import { IPrintedObject } from "../models/Printing"
import { IRoundedRectShape } from "../models/Rectangle"
import {
  BaseComponent,
  IComponentInfo,
  IComponentLabels,
  IComponentShape,
} from "./Base"
import { Glass } from "./Glass"

export class PrintedLogo extends BaseComponent {
  private canvasW: number
  public constructor(
    id: number,
    solutionOfLogo: IPrintedObject,
    glass: Glass,
    canvasW: number,
    dimReference: IComponentShape,
    t: TranslateFunction
  ) {
    const isC = solutionOfLogo.isCircle
    const heightOfLogo = isC
      ? Number(solutionOfLogo.radius * 2)
      : Number(solutionOfLogo.height)
    const widthOfLogo = isC
      ? Number(solutionOfLogo.radius * 2)
      : solutionOfLogo.wImg && solutionOfLogo.img64
      ? Number(
          getSvgWidth(
            heightOfLogo,
            getSvgDimensionsFromBase64(solutionOfLogo.img64)
          )
        )
      : Number(solutionOfLogo.width)
    const radiusOfLogo: IRadius = isC
      ? {
          tr: solutionOfLogo.radius,
          tl: solutionOfLogo.radius,
          br: solutionOfLogo.radius,
          bl: solutionOfLogo.radius,
        }
      : zeroIRadius()

    const offsetY =
      -glass.shape.height / 2 +
      heightOfLogo / 2 +
      solutionOfLogo.positioning.centralOffsetY +
      glass.shape.centralOffsetY
    const offsetX =
      -glass.shape.width / 2 +
      widthOfLogo / 2 +
      solutionOfLogo.positioning.centralOffsetX +
      glass.shape.centralOffsetX
    const colors = solutionOfLogo.colors.filter((c) => c !== "")
    let description = solutionOfLogo.title
    if (colors.length > 0)
      description += ` ${t("in_colors")} ${solutionOfLogo.colors.join(", ")}`
    description += solutionOfLogo.wImg
      ? `, ${t("w_img")}`
      : solutionOfLogo.isHole
      ? `, ${t("is_hole")}`
      : solutionOfLogo.isTransparent
      ? `, ${t("is_transparent")}`
      : ""

    super(
      {
        id,
        color: "#000",
        description,
        img: solutionOfLogo.wImg ? solutionOfLogo.img64 : null,
      } as IComponentInfo,
      {
        width: widthOfLogo,
        height: heightOfLogo,
        centralOffsetX: offsetX,
        centralOffsetY: offsetY,
        radius: radiusOfLogo,
        drawNoRadii: true,
      } as IRoundedRectShape,
      {
        dimensions: {
          sides: [Side.Bottom, Side.Right],
          diff: true,
          reference: dimReference,
        },
        description: false,
      } as IComponentLabels
    )

    this.canvasW = canvasW
  }

  override render() {
    if (this.info.img) {
      return (
        <ImgRectangle
          key={this.info.id}
          shape={this.shape}
          file={this.info.img}
          canvasW={this.canvasW}
        />
      )
    }
    return (
      <RoundedRect key={this.info.id} shape={this.shape} info={this.info} />
    )
  }
}

function getSvgDimensionsFromBase64(base64: string): {
  width?: number
  height?: number
} {
  try {
    const decoded = atob(base64.split(",")[1])
    const parser = new DOMParser()
    const svgDoc = parser.parseFromString(decoded, "image/svg+xml")
    const svgElement = svgDoc.querySelector("svg")

    if (!svgElement) return {}

    const width = svgElement.getAttribute("width")
    const height = svgElement.getAttribute("height")

    // Fallback na viewBox, pokud není width/height
    if (!width || !height) {
      const viewBox = svgElement.getAttribute("viewBox")
      if (viewBox) {
        const [, , w, h] = viewBox.split(" ").map(parseFloat)
        return { width: w, height: h }
      }
    }

    return {
      width: width ? parseFloat(width) : undefined,
      height: height ? parseFloat(height) : undefined,
    }
  } catch (e) {
    console.warn("Failed to parse SVG dimensions:", e)
    return {}
  }
}

function getSvgWidth(
  newW: number,
  og: { width?: number; height?: number }
): number {
  if (og.height === undefined) return 5
  if (og.width === undefined) return 5
  return (og.width / og.height) * newW
}
